import React from 'react';

interface DeadlinePillProps {
  deadline?: string | null;
  /** Show "No deadline set" instead of nothing when empty (used in detail headers). */
  showEmpty?: boolean;
}

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

function formatRelative(deadlineMs: number): { label: string; tone: 'overdue' | 'soon' | 'normal' } {
  const diff = deadlineMs - Date.now();
  if (diff <= 0) {
    const overdueDays = Math.ceil(-diff / ONE_DAY_MS);
    const overdueHours = Math.ceil(-diff / ONE_HOUR_MS);
    const label = -diff < ONE_DAY_MS
      ? `Overdue ${overdueHours}h`
      : `Overdue ${overdueDays}d`;
    return { label, tone: 'overdue' };
  }
  if (diff <= ONE_DAY_MS) {
    const hours = Math.max(1, Math.round(diff / ONE_HOUR_MS));
    return { label: `Due in ${hours}h`, tone: 'soon' };
  }
  const days = Math.round(diff / ONE_DAY_MS);
  return { label: `Due in ${days}d`, tone: 'normal' };
}

const TONE_STYLES: Record<'overdue' | 'soon' | 'normal', React.CSSProperties> = {
  overdue: { background: '#f8d7da', color: '#721c24', borderColor: '#f5c6cb' },
  soon:    { background: '#fff3cd', color: '#856404', borderColor: '#ffeeba' },
  normal:  { background: '#e7f1ff', color: '#123250', borderColor: '#cfe0f7' }
};

const DeadlinePill: React.FC<DeadlinePillProps> = ({ deadline, showEmpty = false }) => {
  if (!deadline) {
    if (!showEmpty) return null;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#f4f4f5', color: '#6b7280',
        fontSize: 11, fontWeight: 500,
        padding: '3px 8px', borderRadius: 10,
        border: '1px solid #e4e4e7'
      }}>
        No deadline
      </span>
    );
  }

  const date = new Date(deadline);
  if (Number.isNaN(date.getTime())) return null;

  const { label, tone } = formatRelative(date.getTime());
  const absolute = date.toLocaleString('en-GB', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
  const style = TONE_STYLES[tone];

  return (
    <span
      title={`Deadline: ${absolute}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: style.background, color: style.color,
        fontSize: 11, fontWeight: 600,
        padding: '3px 8px', borderRadius: 10,
        border: `1px solid ${style.borderColor}`
      }}
    >
      <span aria-hidden style={{ fontSize: 10 }}>&#9201;</span>
      {label}
    </span>
  );
};

export default DeadlinePill;
